/**
 * Royale Spaces - AI-Powered Property Reimagination
 * Type definitions for the Spaces module
 */

export enum DesignStyle {
  Modern = 'Modern',
  MidCenturyModern = 'Mid-Century Modern',
  Tudor = 'Tudor',
  Scandinavian = 'Scandinavian',
  Industrial = 'Industrial',
  Bohemian = 'Bohemian',
  Minimalist = 'Minimalist',
  Farmhouse = 'Farmhouse',
  ArtDeco = 'Art Deco',
  Coastal = 'Coastal',
  Traditional = 'Traditional',
  Japandi = 'Japandi',
  Maximalist = 'Maximalist',
  Rustic = 'Rustic',
  Mediterranean = 'Mediterranean',
  FrenchCountry = 'French Country'
}

export enum ColourPalette {
  Neutral = 'Neutral (Beiges, Creams, Whites)',
  Monochrome = 'Monochrome (Black, White, Grey)',
  Earthy = 'Earthy (Terracotta, Olive, Brown, Sage)',
  Cool = 'Cool (Blues, Teals, Greys, Silver)',
  Warm = 'Warm (Gold, Orange, Red, Yellow)',
  Pastel = 'Pastel (Soft Pink, Mint, Baby Blue)',
  Bold = 'Bold & Vibrant (Jewel Tones, High Contrast)',
  DarkMoody = 'Dark & Moody (Charcoal, Navy, Forest Green)',
  Metallic = 'Metallic (Gold, Silver, Bronze)',
  Organic = 'Organic (Raw Wood, Stone, Cream)',
  Retro = 'Retro (Mustard, Teal, Rust)',
  Berry = 'Berry (Deep Reds, Plums, Purples)'
}

export enum ClutterLevel {
  Minimal = 'Minimal (Clean, sparse, airy)',
  Medium = 'Medium (Lived-in, balanced)',
  Cluttered = 'Maximalist (Busy, collected, eclectic)'
}

export enum TimeOfDay {
  Morning = 'Morning (Soft, cool light)',
  Noon = 'Noon (Bright, harsh shadows)',
  GoldenHour = 'Golden Hour (Warm, glowing)',
  Night = 'Night (Artificial lighting, moody)'
}

export enum ArtworkStyle {
  Abstract = 'Abstract',
  Classical = 'Classical/Oil Paintings',
  Photography = 'Photography (B&W or Colour)',
  PopArt = 'Pop Art',
  Nature = 'Nature/Botanical',
  Surrealism = 'Surrealism',
  Typography = 'Typography/Posters',
  None = 'None/Minimal',
  Impressionist = 'Impressionist (Soft, Painterly)',
  MinimalistLine = 'Minimalist Line Art',
  Geometric = 'Geometric (Bauhaus, Patterns)',
  StreetArt = 'Street Art (Graffiti, Urban)',
  Watercolour = 'Watercolour (Soft, Bleed)',
  Digital = 'Digital Art (Cyberpunk, Glitch)',
  MixedMedia = 'Mixed Media (Collage, Eclectic)',
  Textured = 'Textured Art (3D Relief, Plaster)'
}

export enum RoomType {
  LivingRoom = 'Living Room',
  Bedroom = 'Bedroom',
  Kitchen = 'Kitchen',
  Bathroom = 'Bathroom',
  DiningRoom = 'Dining Room',
  Office = 'Home Office',
  Hallway = 'Hallway/Entry',
  Outdoor = 'Patio/Outdoor'
}

export interface WizardState {
  style: DesignStyle | null;
  colourPalette: ColourPalette | null;
  clutter: ClutterLevel | null;
  timeOfDay: TimeOfDay | null;
  artwork: ArtworkStyle | null;
  roomType: RoomType | null;
  additionalNotes: string;
  image: string | null; // base64
}

export interface StepProps {
  data: WizardState;
  updateData: (key: keyof WizardState, value: WizardState[keyof WizardState]) => void;
  onNext: () => void;
  onBack: () => void;
}

export interface GenerateDesignRequest {
  state: WizardState;
}

export interface GenerateDesignResponse {
  success: boolean;
  imageUrl?: string;
  error?: string;
  remainingUsage?: number;
}

/**
 * Portal-specific types for wishlisted property image selection
 */

export interface PropertyImage {
  id: string;
  url: string;
  thumbnailUrl?: string;
  alt?: string;
  usageContext?: string;
}

export interface WishlistPropertyImage {
  propertyId: string;
  propertyName: string;
  propertyLocation: string;
  images: PropertyImage[];
}

export interface SpacesUsage {
  count: number;
  maxAllowed: number;
  remaining: number;
  canGenerate: boolean;
}

export const SPACES_MAX_USAGE = 5;

export interface SpacesWizardProps {
  /** When 'portal', uses PropertyImagePicker and enforces usage limits */
  mode?: 'standalone' | 'portal';
  /** Wishlisted properties with their images (portal mode only) */
  wishlistProperties?: WishlistPropertyImage[];
  /** Current usage count (portal mode only) */
  usageCount?: number;
  /** Max allowed generations (portal mode only, defaults to 5) */
  maxUsage?: number;
  /** Callback after successful generation (portal mode only) */
  onUsageIncrement?: () => void;
  /** Callback to sync the authoritative server count back to the client */
  onUsageSync?: (serverCount: number) => void;
}
